import { Injectable } from '@angular/core'
import { APP_CONFIG } from '../config/environment'
import { AuthService } from './auth.service'

export type RepaymentExportStatus = 'QUEUED' | 'RUNNING' | 'COMPLETE' | 'FAILED'

export interface RepaymentExport {
  id: string
  startDate: string | null
  endDate: string | null
  includeDeleted: boolean
  status: RepaymentExportStatus
  completedRows: number
  totalRows: number | null
  createdAt: string
  createdBy: string | null
  error: string | null
}

export interface CreateRepaymentExportRequest {
  startDate: string | null
  endDate: string | null
  includeDeleted: boolean
}

/**
 * API contract: POST /repayment-exports, GET /repayment-exports, and
 * GET /repayment-exports/{id}/download. Every response is JSON and uses the
 * Azure bearer token because this is an operational API rather than PostgREST.
 */
@Injectable({ providedIn: 'root' })
export class RepaymentExportService {
  constructor(private readonly authService: AuthService) {}

  isConfigured(): boolean { return Boolean(APP_CONFIG.repaymentExportsApiUrl) }

  async list(signal?: AbortSignal): Promise<RepaymentExport[]> {
    const response = await this.request('', { signal })
    const value = await this.readJson(response)
    if (!response.ok) throw new Error(this.errorMessage(value) ?? 'Failed to load repayment exports.')
    const rows = Array.isArray(value) ? value : this.record(value)?.['items']
    if (!Array.isArray(rows)) throw new Error('Repayment exports response was invalid.')
    return rows.map((row) => this.toExport(row)).filter((row): row is RepaymentExport => row !== null)
  }

  async create(request: CreateRepaymentExportRequest): Promise<RepaymentExport> {
    const response = await this.request('', { method: 'POST', body: JSON.stringify({
      startDate: request.startDate,
      endDate: request.endDate,
      includeDeleted: request.includeDeleted,
    }) })
    const value = await this.readJson(response)
    if (!response.ok) throw new Error(this.errorMessage(value) ?? 'Failed to start repayment export.')
    const result = this.toExport(this.record(value)?.['item'] ?? value)
    if (!result) throw new Error('Repayment export response was invalid.')
    return result
  }

  async download(id: string): Promise<string> {
    const response = await this.request(`/${encodeURIComponent(id)}/download`)
    const value = await this.readJson(response)
    if (!response.ok) throw new Error(this.errorMessage(value) ?? 'Failed to prepare repayment export download.')
    const url = this.record(value)?.['downloadUrl']
    if (typeof url !== 'string' || !url) throw new Error('Repayment export download response was invalid.')
    return url
  }

  async retry(id: string): Promise<RepaymentExport> {
    const response = await this.request(`/${encodeURIComponent(id)}/retry`, { method: 'POST' })
    const value = await this.readJson(response)
    if (!response.ok) throw new Error(this.errorMessage(value) ?? 'Failed to retry export.')
    const item = this.toExport(this.record(value)?.['item'])
    if (!item) throw new Error('Repayment export response was invalid.')
    return item
  }

  async remove(id: string): Promise<void> {
    const response = await this.request(`/${encodeURIComponent(id)}`, { method: 'DELETE' })
    const value = await this.readJson(response)
    if (!response.ok) throw new Error(this.errorMessage(value) ?? 'Failed to remove export.')
  }

  private async request(path: string, init: RequestInit = {}): Promise<Response> {
    if (!APP_CONFIG.repaymentExportsApiUrl) throw new Error('Repayment exports are not configured for this environment.')
    const token = await this.authService.getAzureAccessToken()
    return fetch(`${APP_CONFIG.repaymentExportsApiUrl.replace(/\/+$/, '')}${path}`, {
      ...init,
      headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json', ...init.headers },
    })
  }

  private async readJson(response: Response): Promise<unknown> {
    const body = await response.text()
    if (!body) return null
    try { return JSON.parse(body) } catch { return null }
  }
  private record(value: unknown): Record<string, unknown> | null {
    return value && typeof value === 'object' && !Array.isArray(value) ? value as Record<string, unknown> : null
  }
  private errorMessage(value: unknown): string | null {
    const error = this.record(value)?.['error']
    return typeof error === 'string' ? error : null
  }
  private toExport(value: unknown): RepaymentExport | null {
    const row = this.record(value)
    if (!row || typeof row['id'] !== 'string' || typeof row['status'] !== 'string' || typeof row['createdAt'] !== 'string') return null
    const status = row['status']
    if (!['QUEUED', 'RUNNING', 'COMPLETE', 'FAILED'].includes(status)) return null
    return {
      id: row['id'], status: status as RepaymentExportStatus,
      startDate: this.stringOrNull(row['startDate']), endDate: this.stringOrNull(row['endDate']),
      includeDeleted: row['includeDeleted'] === true,
      completedRows: typeof row['completedRows'] === 'number' ? row['completedRows'] : 0,
      totalRows: typeof row['totalRows'] === 'number' ? row['totalRows'] : null,
      createdAt: row['createdAt'], createdBy: this.stringOrNull(row['createdBy']), error: this.stringOrNull(row['error']),
    }
  }
  private stringOrNull(value: unknown): string | null { return typeof value === 'string' ? value : null }
}
